import {
  Box,
  Divider,
  ModalProps,
  Progress,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import SimplePopup from "components/SimplePopup";
import WalletAsset from "components/WalletAsset";
import useBear from "hooks/useBear";
import { formatEtherBalance } from "utils/numberUtils";
import HoneyIcon from "components/HoneyIcon";
import { useMemo } from "react";
import { calculateHoneyConsumptionRequirement } from "utils/levelUtils";
import TinyHeading from "components/TinyHeading";
import HoneyAmount from "components/HoneyAmount";

type LevelInfoPopupProps = Omit<ModalProps, "children"> & {};

const LevelInfoPopup = ({ onClose, ...rest }: LevelInfoPopupProps) => {
  const translate = useTranslate();
  const { honeyConsumed, levelDetails } = useBear();

  const nextLevelsInfoList = useMemo(() => {
    const items = [];

    if (levelDetails) {
      for (let i = 1; i < 7; i++) {
        const level = levelDetails?.nextLevel + i;
        const honeyRequired = calculateHoneyConsumptionRequirement(level);
        items.push({ level, honeyRequired });
      }
    }

    return items;
  }, [levelDetails]);

  return (
    <SimplePopup
      title={`${translate("common:level")} ${levelDetails?.currentLevel}`}
      description={translate("bearLevel:description")}
      size="sm"
      onClose={onClose}
      {...rest}
    >
      <VStack px="6" pt="4" w="full" spacing="6" pb="3">
        <Box w="full">
          <SimpleGrid columns={1} gap="3">
            <WalletAsset
              icon={HoneyIcon}
              title={translate("bearLevel:honeyEaten")}
              value={honeyConsumed && formatEtherBalance(honeyConsumed)}
            />
            <WalletAsset
              icon={HoneyIcon}
              title={translate("bearLevel:nextLevel")}
              value={
                levelDetails &&
                formatEtherBalance(levelDetails.nextLevelHoneyRequirement)
              }
            />
          </SimpleGrid>

          <Progress
            h="3"
            w="100%"
            colorScheme="secondary"
            value={levelDetails?.nextLevelProgress || 0}
            borderRadius="0"
          />
        </Box>

        <Divider borderBottomColor="bright.500" />

        <SimpleGrid columns={2} gap="3" w="full">
          {nextLevelsInfoList.map(({ level, honeyRequired }) => (
            <Box key={level}>
              <TinyHeading mb="0.5">
                {translate("common:level")} {level}{" "}
              </TinyHeading>
              <Text color="secondary.500">
                <HoneyAmount amount={honeyRequired} />
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    </SimplePopup>
  );
};

export default LevelInfoPopup;
